import { Link } from 'react-router-dom';
import { articles } from '../../../mocks/articles';

const getCategoryColorClasses = (color: string) => {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    purple:  { bg: 'bg-purple-600/20',  text: 'text-purple-400',  border: 'border-purple-500/30' },
    blue:    { bg: 'bg-cyan-600/20',    text: 'text-cyan-400',    border: 'border-cyan-500/30'   },
    green:   { bg: 'bg-emerald-600/20', text: 'text-emerald-400', border: 'border-emerald-500/30'},
  };
  return colors[color] ?? colors.purple;
};

/** Returns the article index that should be featured today (rotates daily). */
function getDailyFeaturedIndex(): number {
  const daysSinceEpoch = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
  return daysSinceEpoch % articles.length;
}

export default function FeaturedGuide() {
  const featured = articles[getDailyFeaturedIndex()];
  if (!featured) return null;

  const colorClasses = getCategoryColorClasses(featured.categoryColor);

  return (
    <section className="py-14 relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute top-0 left-1/3 w-80 h-80 bg-purple-600/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-cyan-500/6 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/30 rounded-full text-xs uppercase tracking-widest text-amber-400/90 bg-amber-400/5 mb-3">
              <i className="ri-fire-fill text-amber-400"></i>
              Featured Guide
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Featured Guide
            </h2>
          </div>
          <Link
            to="/articles"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 border border-white/20 text-white/80 rounded-lg text-sm font-medium hover:border-cyan-400/50 hover:text-cyan-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            All Guides
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Large featured card */}
        <Link to={`/article/${featured.slug}`} className="group block">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-purple-400/30 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/15">
            {/* Top glow bar */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-purple-400/50 to-transparent" />

            <div className="grid lg:grid-cols-2 items-stretch">
              {/* Image side */}
              <div className="relative h-64 lg:h-auto min-h-[340px] overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-black/70 hidden lg:block" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent lg:hidden" />

                {/* Badges */}
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <span className={`inline-block px-3 py-1 ${colorClasses.bg} ${colorClasses.text} rounded-full text-xs font-semibold uppercase tracking-wider border ${colorClasses.border} backdrop-blur-sm`}>
                    {featured.category}
                  </span>
                </div>

                {/* Read time + views */}
                <div className="absolute bottom-5 left-5 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs text-gray-300 border border-white/10">
                    <i className="ri-time-line text-cyan-400"></i>
                    {featured.readTime}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs text-gray-300 border border-white/10">
                    <i className="ri-eye-line text-purple-400"></i>
                    {featured.views}
                  </span>
                </div>
              </div>

              {/* Content side */}
              <div
                className="p-8 lg:p-12 flex flex-col justify-center space-y-6"
                style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(0,0,0,0.65) 60%, rgba(6,182,212,0.05) 100%)' }}
              >
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                    {featured.date}
                  </p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white group-hover:text-purple-300 transition-colors duration-300 leading-snug">
                    {featured.title}
                  </h3>
                </div>

                <p className="text-gray-400 leading-relaxed text-base line-clamp-3">
                  {featured.excerpt}
                </p>

                {/* Highlights */}
                <ul className="space-y-2.5">
                  {[
                    { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-400', text: 'Only verified, currently available Amazon products' },
                    { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-400', text: 'Accurate specs, pros &amp; cons for every pick' },
                    { icon: 'ri-checkbox-circle-fill', color: 'text-emerald-400', text: 'Direct affiliate links, no fake or guessed prices' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${item.color}`}>
                        <i className={item.icon}></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: item.text }} />
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="pt-2">
                  <div className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-purple-600 to-cyan-600 text-white rounded-lg font-bold hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300 group-hover:scale-[1.02] whitespace-nowrap text-sm">
                    <i className="ri-book-open-line"></i>
                    Read Full Guide
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom glow bar */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
          </div>
        </Link>
      </div>
    </section>
  );
}
